import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { ColumnDef, ColumnFiltersState, SortingState, VisibilityState } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { Virtualizer } from "@tanstack/react-virtual";

import { Card } from "../ui";
import { DataTableColumnHeader } from "./DataTableColumnHeader";
import { DataTableEmptyState } from "./DataTableEmptyState";
import { DataTablePagination } from "./DataTablePagination";
import { DataTableRowActions } from "./DataTableRowActions";
import { DataTableSkeleton } from "./DataTableSkeleton";
import { DataTableToolbar } from "./DataTableToolbar";
import { useTableUrlState } from "./useTableUrlState";
import type { DataTableProps } from "./types";

// El sistema de tablas del portal.
//
// # Por qué un solo componente y no una tabla por pantalla
//
// Había dieciséis pantallas pintando `<table>` a mano. Cada una resolvía a su
// manera lo mismo —qué hacer sin datos, cómo paginar, dónde van las
// acciones— y ninguna tenía orden ni filtros, porque añadirlos significaba
// escribirlos dieciséis veces. El coste no era el código repetido: era que
// mejorar una tabla no mejoraba las demás.
//
// # Por qué TanStack Table
//
// Es headless: aporta el motor —orden, filtrado, paginación, selección,
// visibilidad— y no pinta nada, así que el aspecto sigue siendo el de este
// portal y no el de una librería. Lo contrario —escribir el motor a mano—
// son varios cientos de líneas de lógica sutil (orden estable, filtros
// combinados, selección entre páginas) que ya está resuelta y probada.
//
// # Qué NO sabe este componente
//
// Nada de negocio: ni endpoints, ni permisos, ni qué es un tenant. Recibe
// filas, definiciones de columna y acciones; devuelve eventos. Esa frontera
// es lo que permite usarlo en Cuentas y en Llamadas sin tocarlo.

/** Filtro de columna: igualdad exacta contra el valor de la celda. */
function igualdad(row: { getValue: (id: string) => unknown }, columnId: string, filterValue: unknown) {
  if (filterValue === undefined || filterValue === "") return true;
  return String(row.getValue(columnId)) === String(filterValue);
}

export function DataTable<TData>({
  data,
  columns,
  getRowId,
  loading = false,
  error = null,
  onRetry,
  empty,
  search,
  filters = [],
  rowActions = [],
  bulkActions = [],
  selectable,
  pagination,
  pageSize = 25,
  urlStateKey,
  virtualizeFrom,
  maxHeight = "32rem",
  hideableColumns = false,
  toolbarActions,
  onRowClick,
  rowClassName,
  caption,
}: DataTableProps<TData>) {
  const urlState = useTableUrlState(urlStateKey);
  const tablaId = useId().replace(/:/g, "");

  // El estado vive aquí salvo que la pantalla pida llevarlo en la URL. Las
  // dos fuentes no conviven: o manda la URL o manda el componente, porque
  // sincronizar ambas termina en bucles de renderizado.
  const [sortingLocal, setSortingLocal] = useState<SortingState>([]);
  const [filtersLocal, setFiltersLocal] = useState<ColumnFiltersState>([]);
  const [busquedaLocal, setBusquedaLocal] = useState("");
  const [visibility, setVisibility] = useState<VisibilityState>({});
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [paginaLocal, setPaginaLocal] = useState(0);

  // # Por qué estos `useMemo` no son una optimización
  //
  // El estado que sale de la URL se recalcula en cada render, y con estado
  // controlado TanStack lo reconcilia: si el array cambia de identidad cada
  // vez, reconcilia cada vez, y eso es un bucle de renders —no una tabla
  // lenta, una tabla que cuelga el navegador—. Lo descubrió una prueba al
  // teclear en el buscador. La identidad tiene que depender del contenido.
  const sortKey = urlState?.sort ? `${urlState.sort.desc ? "-" : ""}${urlState.sort.id}` : "";
  const sorting: SortingState = useMemo(
    () => (urlState?.sort ? [urlState.sort] : sortingLocal),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- la clave es el contenido
    [sortKey, sortingLocal, urlState === null],
  );

  const filtersKey = urlState ? JSON.stringify(urlState.filters) : "";
  const columnFilters: ColumnFiltersState = useMemo(
    () =>
      urlState ? Object.entries(urlState.filters).map(([id, value]) => ({ id, value })) : filtersLocal,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- idem
    [filtersKey, filtersLocal, urlState === null],
  );

  // # Por qué la búsqueda no escribe en la URL en cada tecla
  //
  // Porque hacerlo convierte cada letra en una navegación: el estado va y
  // vuelve por el router antes de llegar al input, el cursor salta y, con el
  // estado controlado de la tabla, se realimenta. Aquí se teclea contra
  // estado local —respuesta inmediata— y la URL se actualiza un momento
  // después, que es cuando sirve para compartir el enlace.
  const busqueda = search?.value ?? busquedaLocal;

  const conSeleccion = selectable || bulkActions.length > 0;
  const conAcciones = rowActions.length > 0;

  // Las columnas de selección y acciones se añaden aquí y no las declara la
  // pantalla: son parte del armazón, y pedirle a cada módulo que las repita
  // es pedirle que las escriba distinto.
  const columnasFinales = useMemo<ColumnDef<TData, any>[]>(() => {
    const out: ColumnDef<TData, any>[] = [];

    if (conSeleccion) {
      out.push({
        id: "__select",
        enableSorting: false,
        enableHiding: false,
        meta: { width: "2.5rem", label: "Selección" },
        header: ({ table }) => (
          <input
            type="checkbox"
            aria-label="Seleccionar todas las filas de esta página"
            checked={table.getIsAllPageRowsSelected()}
            ref={(el) => {
              if (el) el.indeterminate = table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected();
            }}
            onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label="Seleccionar esta fila"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      });
    }

    out.push(...columns);

    if (conAcciones) {
      out.push({
        id: "__actions",
        enableSorting: false,
        enableHiding: false,
        meta: { width: "3rem", align: "right", label: "Acciones" },
        header: () => <span className="sr-only">Acciones</span>,
        cell: ({ row }) => <DataTableRowActions row={row.original} actions={rowActions} />,
      });
    }

    return out;
  }, [columns, conSeleccion, conAcciones, rowActions]);

  const filas = useMemo(() => data ?? [], [data]);

  const table = useReactTable({
    data: filas,
    columns: columnasFinales,
    getRowId: (row) => getRowId(row),
    state: {
      sorting,
      columnFilters,
      globalFilter: busqueda,
      columnVisibility: visibility,
      rowSelection: selection,
      // Solo con paginación en cliente: con la de servidor, la página la
      // manda quien trae los datos y el motor no debe recortar nada.
      ...(pagination === "client"
        ? { pagination: { pageIndex: urlState?.page ?? paginaLocal, pageSize } }
        : {}),
    },
    onSortingChange: (updater) => {
      const siguiente = typeof updater === "function" ? updater(sorting) : updater;
      if (urlState) urlState.setSort(siguiente[0] ?? null);
      else setSortingLocal(siguiente);
    },
    onColumnFiltersChange: (updater) => {
      const siguiente = typeof updater === "function" ? updater(columnFilters) : updater;
      if (urlState) {
        // Se escriben los que cambiaron y se borran los que desaparecieron.
        const previos = new Set(columnFilters.map((f) => f.id));
        for (const filtro of siguiente) {
          urlState.setFilter(filtro.id, filtro.value === undefined ? undefined : String(filtro.value));
          previos.delete(filtro.id);
        }
        for (const id of previos) urlState.setFilter(id, undefined);
      } else {
        setFiltersLocal(siguiente);
      }
    },
    onGlobalFilterChange: (valor: string) => {
      if (search?.onChange) search.onChange(valor);
      else setBusquedaLocal(valor);
    },
    onColumnVisibilityChange: setVisibility,
    onRowSelectionChange: setSelection,
    enableRowSelection: conSeleccion,
    filterFns: { igualdad },
    defaultColumn: { filterFn: igualdad as any },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    // La paginación en cliente solo se monta si se pide: con paginación de
    // servidor, el motor no debe recortar nada —la página ya viene recortada—
    // y hacerlo escondería filas que el backend sí mandó.
    ...(pagination === "client"
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          onPaginationChange: (updater: any) => {
            const previo = { pageIndex: urlState?.page ?? paginaLocal, pageSize };
            const siguiente = typeof updater === "function" ? updater(previo) : updater;
            if (urlState) urlState.setPage(siguiente.pageIndex);
            else setPaginaLocal(siguiente.pageIndex);
          },
        }
      : {}),
  });

  // Lo que venga en la URL al entrar es el punto de partida del buscador: un
  // enlace compartido con `?cuentas_q=lerma` tiene que llegar con el filtro
  // puesto y el texto visible en el campo.
  const urlSearch = urlState?.search ?? "";
  useEffect(() => {
    setBusquedaLocal(urlSearch);
    // Solo al montar, y solo si la URL traía algo: después manda lo que se
    // teclea, o cada cambio de URL pisaría la escritura en curso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Y el camino de vuelta, con un respiro: se escribe en la URL cuando se
  // deja de teclear.
  const setSearchEnURL = urlState?.setSearch;
  useEffect(() => {
    if (!setSearchEnURL || search?.onChange) return;
    if (busquedaLocal === urlSearch) return;
    const id = setTimeout(() => setSearchEnURL(busquedaLocal), 300);
    return () => clearTimeout(id);
  }, [busquedaLocal, urlSearch, setSearchEnURL, search?.onChange]);

  const filtrada = busqueda.trim() !== "" || columnFilters.length > 0;
  const limpiarFiltros = useCallback(() => {
    setBusquedaLocal("");
    if (urlState) urlState.clear();
    else setFiltersLocal([]);
    search?.onChange?.("");
  }, [urlState, search]);

  const filasVisibles = table.getRowModel().rows;
  const columnasVisibles = table.getVisibleLeafColumns();

  // --- virtualización ------------------------------------------------------
  const contenedor = useRef<HTMLDivElement>(null);
  const virtualizar = virtualizeFrom !== undefined && filasVisibles.length >= virtualizeFrom;
  const virtualizer = useVirtualizer({
    count: filasVisibles.length,
    getScrollElement: () => contenedor.current,
    estimateSize: () => 45,
    overscan: 12,
    enabled: virtualizar,
  });

  // Las etiquetas de columna para el apilado en móvil, igual que hacía la
  // tabla antigua: cada celda muestra «columna ··· valor» sin que la pantalla
  // repita el nombre en cada fila.
  const css = useMemo(() => {
    const reglas = columnasVisibles
      .map((column, i) => {
        const meta = column.columnDef.meta;
        const etiqueta = meta?.label;
        const oculta = meta?.hideOnMobile;
        const selector = `.${tablaId} td:nth-child(${i + 1})`;
        if (oculta) return `${selector},.${tablaId} th:nth-child(${i + 1}){display:none}`;
        return etiqueta ? `${selector}::before{content:${JSON.stringify(etiqueta)}}` : "";
      })
      .join("");
    return reglas ? `@media (max-width:767px){${reglas}}` : "";
  }, [columnasVisibles, tablaId]);

  if (loading && data === null) {
    return (
      <Card className="overflow-hidden">
        <DataTableSkeleton columns={Math.min(columnasFinales.length, 6)} />
      </Card>
    );
  }

  if (error !== null && (data === null || data.length === 0)) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-rose-200">{error}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 rounded-lg border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-slate-200 transition hover:bg-white/[0.09]"
          >
            Reintentar
          </button>
        )}
      </Card>
    );
  }

  return (
    <div>
      <DataTableToolbar
        table={table}
        search={
          search !== undefined || urlStateKey !== undefined
            ? {
                placeholder: search?.placeholder,
                value: busqueda,
                onChange: (valor) => table.setGlobalFilter(valor),
              }
            : undefined
        }
        filters={filters}
        bulkActions={bulkActions}
        hideableColumns={hideableColumns}
        actions={toolbarActions}
      />

      {/* Un error con datos en pantalla es un aviso, no un reemplazo: lo que
          se ve sigue siendo válido, solo que puede estar desactualizado. */}
      {error !== null && (
        <Card className="mb-3 border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
          No se pudo actualizar: {error}
        </Card>
      )}

      <Card className="overflow-hidden">
        <style>{css}</style>
        <div
          ref={contenedor}
          className="overflow-x-auto"
          style={virtualizar ? { maxHeight, overflowY: "auto" } : undefined}
        >
          <table className={`tabla-apilada w-full text-left text-sm ${tablaId}`}>
            {caption && <caption className="sr-only">{caption}</caption>}
            <thead className="border-b border-white/10 bg-white/[0.04]">
              {table.getHeaderGroups().map((grupo) => (
                <tr key={grupo.id}>
                  {grupo.headers.map((header) => {
                    const meta = header.column.columnDef.meta;
                    const orden = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        scope="col"
                        style={meta?.width ? { width: meta.width } : undefined}
                        aria-sort={
                          orden === "asc" ? "ascending" : orden === "desc" ? "descending" : undefined
                        }
                        className={`px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-400 ${
                          meta?.align === "right"
                            ? "text-right"
                            : meta?.align === "center"
                              ? "text-center"
                              : ""
                        }`}
                      >
                        {header.isPlaceholder
                          ? null
                          : header.column.getCanSort()
                            ? <DataTableColumnHeader
                                column={header.column}
                                title={
                                  meta?.label ??
                                  (typeof header.column.columnDef.header === "string"
                                    ? header.column.columnDef.header
                                    : header.column.id)
                                }
                              />
                            : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody className="divide-y divide-white/5 [&>tr]:transition-colors [&>tr:hover]:bg-white/[0.03]">
              {filasVisibles.length === 0 ? (
                <tr>
                  <td colSpan={columnasVisibles.length}>
                    <DataTableEmptyState
                      title={empty?.title ?? "Sin resultados"}
                      description={empty?.description}
                      action={empty?.action}
                      filtered={filtrada}
                      onClearFilters={limpiarFiltros}
                    />
                  </td>
                </tr>
              ) : virtualizar ? (
                <VirtualBody
                  filas={filasVisibles}
                  virtualizer={virtualizer}
                  columnas={columnasVisibles.length}
                  onRowClick={onRowClick}
                  rowClassName={rowClassName}
                />
              ) : (
                filasVisibles.map((row) => (
                  <Fila
                    key={row.id}
                    row={row}
                    onRowClick={onRowClick}
                    rowClassName={rowClassName}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {pagination !== undefined && (
        <DataTablePagination
          table={table}
          server={pagination === "client" ? undefined : pagination}
          visibles={filasVisibles.length}
        />
      )}
    </div>
  );
}

/** Una fila. Aparte para que React no repinte todas al cambiar una. */
function Fila<TData>({
  row,
  onRowClick,
  rowClassName,
  style,
}: {
  row: any;
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
  style?: React.CSSProperties;
}) {
  const clicable = onRowClick !== undefined;
  return (
    <tr
      data-state={row.getIsSelected() ? "selected" : undefined}
      className={`${row.getIsSelected() ? "bg-sky-500/[0.07]" : ""} ${clicable ? "cursor-pointer" : ""} ${rowClassName?.(row.original) ?? ""}`}
      style={style}
      // Clicable también con teclado: una fila que solo responde al ratón
      // deja fuera a quien navega con tabulador.
      //
      // **Sin `role="button"`.** Lo tenía, y era un error: el rol explícito
      // tapa el implícito, así que la fila dejaba de ser una fila —ni los
      // lectores de pantalla la recorrían como tabla, ni `getAllByRole("row")`
      // la encontraba—. Una fila clicable sigue siendo una fila; lo que la
      // hace alcanzable con teclado es el `tabIndex`, y lo que la hace
      // operable, el `onKeyDown` de aquí abajo. Cuando hace falta anunciar la
      // acción, la pantalla pone además una entrada en el menú de la fila.
      tabIndex={clicable ? 0 : undefined}
      onClick={clicable ? () => onRowClick(row.original) : undefined}
      onKeyDown={
        clicable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick(row.original);
              }
            }
          : undefined
      }
    >
      {row.getVisibleCells().map((cell: any) => {
        const meta = cell.column.columnDef.meta;
        return (
          <td
            key={cell.id}
            className={`px-4 py-2.5 ${
              meta?.align === "right" ? "text-right" : meta?.align === "center" ? "text-center" : ""
            }`}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );
}

/**
 * Cuerpo virtualizado: solo pinta las filas que caben en pantalla.
 *
 * Las filas que faltan se compensan con dos filas vacías de la altura justa,
 * arriba y abajo, para que la barra de desplazamiento mida lo que mediría la
 * tabla entera.
 */
function VirtualBody<TData>({
  filas,
  virtualizer,
  columnas,
  onRowClick,
  rowClassName,
}: {
  filas: any[];
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  columnas: number;
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
}) {
  const items = virtualizer.getVirtualItems();
  const antes = items.length > 0 ? items[0]!.start : 0;
  const despues =
    items.length > 0 ? virtualizer.getTotalSize() - items[items.length - 1]!.end : 0;

  return (
    <>
      {antes > 0 && (
        <tr aria-hidden>
          <td colSpan={columnas} style={{ height: antes, padding: 0 }} />
        </tr>
      )}
      {items.map((item) => (
        <Fila
          key={filas[item.index].id}
          row={filas[item.index]}
          onRowClick={onRowClick}
          rowClassName={rowClassName}
        />
      ))}
      {despues > 0 && (
        <tr aria-hidden>
          <td colSpan={columnas} style={{ height: despues, padding: 0 }} />
        </tr>
      )}
    </>
  );
}
